import * as PIXI from 'pixi.js';

import type Luminary from './';

export class CanvasReusableTexturesManager {
    #luminary: Luminary;
    #detachedTextures: PIXI.Texture[] = [];
    #attachedTextures = new Map<PIXI.Texture, () => void>();

    constructor(luminary: Luminary) {
        this.#luminary = luminary;
    }

    allocateTexture(detach: () => void): PIXI.Texture {
        const texture =
            this.#detachedTextures.pop() ||
            (() => {
                const canvas = document.createElement('canvas');
                const canvasResolution = this.#luminary.outputResolution;
                canvas.width = canvasResolution.width;
                canvas.height = canvasResolution.height;
                canvas.style.width = this.#luminary.outputResolution.width + 'px';
                canvas.style.height = this.#luminary.outputResolution.height + 'px';
                const texture = PIXI.Texture.from(canvas);
                texture.baseTexture.setSize(
                    this.#luminary.referenceResolution.width,
                    this.#luminary.referenceResolution.height,
                );
                return texture;
            })();
        this.#attachedTextures.set(texture, detach);
        return texture;
    }

    detachTextures() {
        for (const [texture, detach] of this.#attachedTextures) {
            detach();

            // destroy() runs this after assets (and their textures) are torn down; a destroyed
            // texture can't be re-framed or recycled.
            if (texture.destroyed) {
                continue;
            }

            const textureWidth = this.#luminary.referenceResolution.width;
            const textureHeight = this.#luminary.referenceResolution.height;
            const rectangle = new PIXI.Rectangle(0, 0, textureWidth, textureHeight);
            texture.orig = rectangle;
            texture.frame = rectangle;
            texture.trim = rectangle;
            this.#detachedTextures.unshift(texture);
        }
        this.#attachedTextures.clear();
    }

    destroy() {
        this.detachTextures();
        for (const texture of this.#detachedTextures) {
            texture.destroy(true);
        }

        this.#detachedTextures = [];
    }

    getDebugInfo() {
        return {
            detachedTextures: this.#detachedTextures,
            attachedTextures: this.#attachedTextures,
        };
    }
}
